import React, { FC, useRef, useEffect, useCallback } from "react";
import IntlTelInput, {
  type IntlTelInputRef,
} from "intl-tel-input/reactWithUtils";
import "intl-tel-input/build/css/intlTelInput.min.css";

import en from "intl-tel-input/i18n/en";
import fr from "intl-tel-input/i18n/fr";
import nl from "intl-tel-input/i18n/nl";

import { Box } from "@UI/Components/Box/Box";
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from "@UI/Components/Form/Form";

const ITI_DICTIONARIES: Record<string, Record<string, string>> = { en, fr, nl };

export const getPhoneInputDictionary = (locale: string): Record<string, string> =>
  ITI_DICTIONARIES[locale.split("-")[0]] ?? en;

export interface PhoneInputProps {
  label: string;
  id: string;
  initialValue?: string;
  onChange: (e164: string) => void;
  onBlur?: () => void;
  onValidityChange?: (valid: boolean) => void;
  isError?: boolean;
  errorMessage?: string;
  initialCountry?: string;
  height?: string;
  disabled?: boolean;
  /** App locale (e.g. `nl-BE`), used to pick the country-name dictionary. Defaults to English. */
  locale?: string;
}

export const PhoneInput: FC<PhoneInputProps> = ({
  label,
  id,
  initialValue = "",
  onChange,
  onBlur,
  onValidityChange,
  isError = false,
  errorMessage = "",
  initialCountry = "be",
  height,
  disabled = false,
  locale,
}) => {
  const i18n = locale ? getPhoneInputDictionary(locale) : undefined;

  const borderColor = isError
    ? "var(--chakra-colors-red-500)"
    : "var(--chakra-colors-gray-400)";

  const itiRef = useRef<IntlTelInputRef | null>(null);

  // Stable no-ops for unused intl-tel-input callbacks.
  // IntlTelInput's internal `update` function lists all four callbacks as deps of a
  // useCallback, so any unstable reference (like the library's own `() => {}` defaults)
  // causes the effect to re-run on every render → infinite loop.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noopCountry = useCallback(() => {}, []);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noopErrorCode = useCallback(() => {}, []);

  // When the prefilled value arrives asynchronously (currentUser loads), update
  // the iti instance directly instead of remounting (remount causes a DOM conflict
  // because intl-tel-input wraps the <input> in a container outside React's tree).
  useEffect(() => {
    if (initialValue) {
      itiRef.current?.getInstance()?.setNumber(initialValue);
    }
  }, [initialValue]);

  return (
    <FormControl isInvalid={isError} color="gray.500">
      <FormLabel color="gray.700" htmlFor={id}>
        {label}
      </FormLabel>
      <Box
        sx={{
          ".iti": { width: "100%" },
          ".iti__selected-country": {
            boxShadow: "none",
            "&:hover": { backgroundColor: "transparent" },
          },
          ".iti__selected-country-primary": {
            borderRadius: "10px 0 0 10px",
            backgroundColor: "#EBEBEB",
            paddingRight: "15px",
            paddingLeft: "15px",
            "&:hover": { backgroundColor: "#DEDEDE" },
          },
          ".iti__selected-dial-code": { marginLeft: "10px" },
          ".iti__search-input": {
            width: "calc(100% - 16px)",
            margin: "8px",
            backgroundColor: "white",
            border: `1px solid ${borderColor}`,
            borderRadius: "var(--chakra-radii-md)",
            padding: "8px 12px",
            fontSize: "var(--chakra-fontSizes-md)",
            color: "var(--chakra-colors-gray-700)",
            outline: "none",
            "&:focus": { borderColor: "var(--chakra-colors-gray-700)" },
          },
        }}
      >
        <IntlTelInput
          ref={itiRef}
          initialValue={initialValue}
          onChangeNumber={onChange}
          onChangeValidity={onValidityChange}
          onChangeCountry={noopCountry}
          onChangeErrorCode={noopErrorCode}
          disabled={disabled}
          inputProps={{
            id,
            onBlur,
            style: {
              width: "100%",
              ...(height && { height }),
              backgroundColor: "#FFFFFF",
              border: `1px solid ${borderColor}`,
              borderRadius: "10px",
              padding: "9px 15px",
              fontSize: "16px",
              fontWeight: "400",
              color: "#666666",
              outline: "none",
            },
          }}
          initOptions={{
            initialCountry,
            separateDialCode: true,
            strictMode: true,
            ...(i18n && { i18n }),
          }}
        />
      </Box>
      {isError && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </FormControl>
  );
};
